import { useEffect, useRef, useState } from "react";
import { X, Hand, Mic, MicOff, Crown, Users, LogOut, Volume2 } from "lucide-react";
import { Avatar } from "./Avatar";
import { ChatSocket } from "../lib/ws";
import { useStore } from "../lib/store";

interface AudioRoomState {
  id: string;
  chat_id: string;
  title: string;
  moderators: string[];
  speakers: string[];
  listeners: string[];
  raised_hands: string[];
  participants: Record<string, { id: string; name: string; avatar_url?: string | null }>;
  active: boolean;
}

interface AudioRoomProps {
  roomId: string;
  chatId: string;
  onClose: () => void;
}

export function AudioRoom({ roomId, chatId: _chatId, onClose }: AudioRoomProps) {
  const me = useStore((s) => s.user);
  const wsRef = useRef<ChatSocket | null>(null);
  const [state, setState] = useState<AudioRoomState | null>(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const ws = new ChatSocket("audio", roomId);
    wsRef.current = ws;
    const off = ws.on((e) => {
      if (e.type === "audio_state") setState(e.state as AudioRoomState);
    });
    ws.connect();
    return () => { off(); ws.close(); };
  }, [roomId]);

  function raise() { wsRef.current?.send({ type: "audio_raise" }); }
  function lower() { wsRef.current?.send({ type: "audio_lower" }); }
  function toggleMute() {
    const next = !muted;
    setMuted(next);
    wsRef.current?.send({ type: "audio_mute", muted: next });
  }
  function promote(userId: string) { wsRef.current?.send({ type: "audio_promote", user_id: userId }); }
  function demote(userId: string) { wsRef.current?.send({ type: "audio_demote", user_id: userId }); }

  if (!state) {
    return (
      <div className="fixed inset-0 z-50 grid place-items-center" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}>
        <div className="glass-strong p-6 animate-pulse-soft">Подключаемся к комнате…</div>
      </div>
    );
  }

  const isMod = state.moderators.includes(me?.id || "");
  const isSpeaker = state.speakers.includes(me?.id || "");
  const handUp = state.raised_hands.includes(me?.id || "");
  const all = [...state.speakers, ...state.listeners];

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}>
      <div className="glass-strong w-full max-w-3xl p-6 animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-accent-300 uppercase tracking-wider">Audio Room · LIVE</div>
            <div className="font-display text-2xl font-bold">{state.title}</div>
          </div>
          <button className="btn btn-ghost p-2 rounded-full" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="mb-6">
          <div className="text-xs uppercase tracking-wider text-ink-400 mb-2">Спикеры ({state.speakers.length})</div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
            {state.speakers.map((id) => {
              const p = state.participants[id];
              if (!p) return null;
              return (
                <div key={id} className="flex flex-col items-center text-center gap-1">
                  <div className="relative">
                    <Avatar name={p.name} url={p.avatar_url} size={56} />
                    {state.moderators.includes(id) && <Crown size={12} className="absolute -top-1 -right-1 text-amber-400" />}
                    <Volume2 size={12} className="absolute -bottom-1 -right-1 text-emerald-400" />
                  </div>
                  <div className="text-xs truncate w-full">{p.name}</div>
                  {isMod && id !== me?.id && (
                    <button className="btn btn-ghost text-[10px] px-2 py-0.5" onClick={() => demote(id)}>Снять</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mb-6">
          <div className="text-xs uppercase tracking-wider text-ink-400 mb-2">Слушатели ({state.listeners.length})</div>
          <div className="flex flex-wrap gap-3">
            {state.listeners.map((id) => {
              const p = state.participants[id];
              if (!p) return null;
              const handed = state.raised_hands.includes(id);
              return (
                <div key={id} className="flex flex-col items-center gap-1">
                  <div className="relative">
                    <Avatar name={p.name} url={p.avatar_url} size={44} />
                    {handed && <Hand size={12} className="absolute -top-1 -right-1 text-amber-300" />}
                  </div>
                  <div className="text-[10px] text-ink-300 max-w-[64px] truncate">{p.name}</div>
                  {isMod && handed && (
                    <button className="btn btn-primary text-[10px] px-2 py-0.5" onClick={() => promote(id)}>На сцену</button>
                  )}
                </div>
              );
            })}
            {state.listeners.length === 0 && <div className="text-sm text-ink-400">Пока никого</div>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isSpeaker ? (
            <button className={`btn ${muted ? "btn-ghost" : "btn-primary"}`} onClick={toggleMute}>
              {muted ? <><MicOff size={14} /> Включить микро</> : <><Mic size={14} /> Замьютить</>}
            </button>
          ) : handUp ? (
            <button className="btn btn-ghost" onClick={lower}><Hand size={14} /> Опустить руку</button>
          ) : (
            <button className="btn btn-ghost" onClick={raise}><Hand size={14} /> Поднять руку</button>
          )}
          <span className="pill"><Users size={12} /> {all.length} участн.</span>
          <div className="flex-1" />
          <button className="btn btn-danger" onClick={onClose}><LogOut size={14} /> Покинуть</button>
        </div>
      </div>
    </div>
  );
}
